/**
 * Game Over Panel
 * Replaces "Deal New Game" once the table's game is over: who won (last
 * player standing), or — if you're out while others are still in — the
 * standings, plus a button to start a fresh game.
 */

import LobbyButton from './LobbyButton';
import React, { useState } from 'react';
import { TableOver } from '../../store/types';
import { formatChips } from '../../utils/format';

interface GameOverPanelProps {
  tableOver: TableOver;
  youId?: string;
  onNewGame: () => Promise<void>;
  /** Leave for the lobby instead. */
  onLobby?: () => Promise<void>;
}

const GameOverPanel: React.FC<GameOverPanelProps> = ({ tableOver, youId, onNewGame, onLobby }) => {
  const [starting, setStarting] = useState(false);
  const { winner, standings } = tableOver;
  const youWon = !!winner && winner.userId === youId;
  const youAreIn = standings.some(s => s.userId === youId);
  const winnerChips = winner ? standings.find(s => s.userId === winner.userId)?.chips : undefined;

  const title = youWon ? 'You won!' : winner ? `${winner.username} won` : "You're out of chips";
  const subtitle = youWon
    ? `Last player standing${winnerChips !== undefined ? ` with ${formatChips(winnerChips)} chips` : ''}.`
    : winner
      ? `Last player standing${winnerChips !== undefined ? ` with ${formatChips(winnerChips)} chips` : ''}.${youAreIn ? '' : " You're out of chips."}`
      : 'Still in:';

  return (
    <div
      data-testid="game-over"
      data-winner={winner?.userId ?? ''}
      className={`p-3 sm:p-4 rounded-xl border text-center space-y-2 ${
        youWon ? 'bg-yellow-900/30 border-yellow-400' : 'bg-gray-900/70 border-gray-700'
      }`}
    >
      <div className="text-3xl">{youWon || winner ? '🏆' : '🪙'}</div>
      <div className={`text-lg sm:text-xl font-bold ${youWon ? 'text-yellow-300' : 'text-white'}`}>{title}</div>
      <div className="text-xs sm:text-sm text-gray-400">{subtitle}</div>

      {!winner && standings.length > 0 && (
        <ol className="text-left text-xs space-y-0.5 max-w-[240px] mx-auto">
          {standings.map((s, i) => (
            <li key={s.userId} className="flex justify-between gap-2">
              <span className="text-gray-300">
                {i + 1}. {s.userId === youId ? 'You' : s.username}
                {i === 0 && <span className="text-yellow-400"> · leader</span>}
              </span>
              <span className="text-yellow-400">{formatChips(s.chips)}</span>
            </li>
          ))}
        </ol>
      )}

      <div className="flex gap-2">
      <button
        data-testid="new-game"
        onClick={async () => {
          setStarting(true);
          try {
            await onNewGame();
          } catch {
            setStarting(false);
          }
        }}
        disabled={starting}
        className="flex-1 py-3 bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 disabled:opacity-60 text-white font-bold rounded-xl shadow-lg transition-all"
      >
        {starting ? 'Starting…' : 'New game'}
      </button>
      {onLobby && <LobbyButton onLeave={onLobby} confirm={false} className="w-[30%] py-3" />}
      </div>
    </div>
  );
};

export default GameOverPanel;
